/**
 * src/components/PaymentForm.jsx — the embedded deposit card form.
 *
 * Built on the Checkout Sessions API: the server creates a Session with
 * `ui_mode: 'elements'` and we mount Stripe's Elements against its client
 * secret. That is Stripe's current recommendation, and it replaces an earlier
 * version here that created a PaymentIntent directly — one API now drives both
 * the embedded form and the hosted redirect, so there is one set of metadata
 * and one webhook event instead of two of each.
 *
 * The card fields are iframes served by Stripe, so the card number is never in
 * our DOM, our JavaScript or our Worker. That is what keeps the app within PCI
 * SAQ A. Do not replace the Element with your own `<input>`: that single change
 * moves the whole application into a heavier compliance category.
 *
 * The trade-off of embedding, and the reason Stripe's hosted page exists: the
 * failure states are ours to handle. Declines, expired cards and the 3-D Secure
 * step-up all surface here rather than on Stripe's page.
 */
import {
  CheckoutElementsProvider,
  PaymentElement,
  useCheckoutElements,
} from '@stripe/react-stripe-js/checkout';
import { useEffect, useState } from 'react';

import { formatMoney } from '../lib/format.js';
import { getStripe } from '../lib/stripe.js';
import { onModeChange, stripeElementsAppearance } from '../lib/theme.js';
import { ErrorBanner } from './ui.jsx';

/**
 * Embedded deposit payment.
 *
 * @param {object} props
 * @param {string} props.publishableKey Stripe publishable key, from the API.
 * @param {string} props.clientSecret Checkout Session client secret.
 * @param {number} props.amount Deposit in minor units, for the button label.
 * @param {string} props.currency
 * @param {string} props.accentColor Studio accent, themed into the Element.
 * @param {() => void} props.onSuccess Called on success without a redirect.
 * @returns {JSX.Element}
 */
export default function PaymentForm({
  publishableKey,
  clientSecret,
  amount,
  currency,
  accentColor,
  onSuccess,
}) {
  // Elements render in Stripe's own cross-origin iframe and can't see this
  // page's CSS, so the appearance is built from the resolved tokens at mount
  // (see lib/theme.js) and pushed again by CardFields when the mode flips.
  // `accentColor` (the studio's own safe-derived accent) still themes
  // colorPrimary — a client shouldn't be able to tell where the studio's
  // page stops and Stripe's iframe starts.
  const [appearance] = useState(() => stripeElementsAppearance({ primary: accentColor }));

  return (
    <CheckoutElementsProvider
      stripe={getStripe(publishableKey)}
      options={{ clientSecret, elementsOptions: { appearance } }}
    >
      <CardFields amount={amount} currency={currency} accentColor={accentColor} onSuccess={onSuccess} />
    </CheckoutElementsProvider>
  );
}

/**
 * The inner form. Separate component because `useCheckoutElements` only works
 * inside the provider.
 *
 * @param {{ amount: number, currency: string, accentColor?: string, onSuccess: () => void }} props
 * @returns {JSX.Element}
 */
function CardFields({ amount, currency, accentColor, onSuccess }) {
  const result = useCheckoutElements();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const checkout = result.type === 'success' ? result.checkout : null;

  // Keep the card fields in step with the light/dark mode after mount.
  useEffect(() => {
    if (!checkout) return undefined;
    return onModeChange(() =>
      checkout.changeAppearance(stripeElementsAppearance({ primary: accentColor })),
    );
  }, [checkout, accentColor]);

  // The hook reports its own lifecycle, so the session loading and failing are
  // states to render rather than things to guess at.
  if (result.type === 'loading') {
    return <p className="muted small loading-pulse">Loading payment form…</p>;
  }
  if (result.type === 'error') {
    return (
      <ErrorBanner>
        {result.error.message || 'The payment form could not be loaded. Please try again.'}
      </ErrorBanner>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);

    // No returnUrl here: the Checkout Session already has return_url set
    // from creation (services/stripe.js's createDepositCheckout) — confirm()
    // rejects a second one ("You cannot provide returnUrl to confirm() when
    // return_url was already provided when creating the Checkout Session").
    const confirmation = await checkout.confirm({
      // Only leave the page when the payment method genuinely requires it — a
      // 3-D Secure challenge or a bank app. A plain card payment resolves
      // here, so the client stays on the booking page throughout.
      redirect: 'if_required',
    });

    if (confirmation.type === 'error') {
      // Stripe writes card and validation errors for the person paying
      // ("Your card was declined"). Anything else is our problem or Stripe's,
      // and its raw message is no use to a client mid-booking.
      const { type, message } = confirmation.error;
      setError(
        type === 'card_error' || type === 'validation_error'
          ? message
          : 'Something went wrong taking the payment. Your card has not been charged.',
      );
      setBusy(false);
      return;
    }

    // No redirect was needed and the payment is through. The appointment is
    // still confirmed by the webhook rather than by this callback — a client
    // closing the tab at this instant must not lose a paid booking.
    onSuccess?.();
  };

  return (
    <form className="stack" onSubmit={submit}>
      <PaymentElement />
      <ErrorBanner>{error}</ErrorBanner>
      <button type="submit" className="btn btn-primary" disabled={busy}>
        {busy ? 'Taking payment…' : `Pay ${formatMoney(amount, currency)} deposit`}
      </button>
      <p className="muted small">
        Card details are handled by Stripe and never touch this studio&rsquo;s systems. Your
        deposit comes off the price on the day.
      </p>
    </form>
  );
}
